/**
 * SynthAudio - Procedural audio synthesis using Web Audio API
 * Generates all game sounds programmatically for the "Tactile Minimalism" aesthetic.
 */

/**
 * Synthesizes game audio in real-time using Web Audio API
 */
export class SynthAudio {
  private context: AudioContext | null = null;
  private masterGain: GainNode | null = null;
  private noiseSource: AudioBufferSourceNode | null = null;

  private sfxVolume: number = 0.6;

  constructor() {
    // Context created on first user interaction
  }

  /**
   * Initialize audio context (must be called from user gesture)
   */
  init(): boolean {
    if (this.context) return true;

    try {
      this.context = new (window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext)();
      this.masterGain = this.context.createGain();
      this.masterGain.connect(this.context.destination);
      this.masterGain.gain.value = 1.0;
      return true;
    } catch (e) {
      console.warn('Web Audio API not available:', e);
      return false;
    }
  }

  /**
   * Resume audio context if suspended
   */
  async resume(): Promise<void> {
    if (this.context?.state === 'suspended') {
      await this.context.resume();
    }
  }

  // ============= Ambient Audio (disabled) =============

  /**
   * Start ambient audio (no-op - no background music)
   */
  startAmbient(): void {
    // No ambient music - game uses only SFX
  }

  /**
   * Stop ambient audio (no-op)
   */
  stopAmbient(): void {
    // No ambient music to stop
  }

  /**
   * Play chime (disabled - no ambient sounds)
   */
  playChime(): void {
    // Disabled - no ambient chimes
  }

  // ============= SFX =============

  /**
   * Impact sound - satisfying wooden "tock"
   */
  playImpact(): void {
    if (!this.context || !this.masterGain) return;

    // Layer 1: Body thump
    const osc1 = this.context.createOscillator();
    const gain1 = this.context.createGain();

    osc1.type = 'sine';
    osc1.frequency.setValueAtTime(150, this.context.currentTime);
    osc1.frequency.exponentialRampToValueAtTime(60, this.context.currentTime + 0.06);

    gain1.gain.setValueAtTime(0.3 * this.sfxVolume, this.context.currentTime);
    gain1.gain.exponentialRampToValueAtTime(0.001, this.context.currentTime + 0.1);

    osc1.connect(gain1);
    gain1.connect(this.masterGain);
    osc1.start();
    osc1.stop(this.context.currentTime + 0.1);

    // Layer 2: Click attack
    const osc2 = this.context.createOscillator();
    const gain2 = this.context.createGain();
    const filter2 = this.context.createBiquadFilter();

    osc2.type = 'triangle';
    osc2.frequency.value = 800;

    filter2.type = 'highpass';
    filter2.frequency.value = 400;

    gain2.gain.setValueAtTime(0.15 * this.sfxVolume, this.context.currentTime);
    gain2.gain.exponentialRampToValueAtTime(0.001, this.context.currentTime + 0.02);

    osc2.connect(filter2);
    filter2.connect(gain2);
    gain2.connect(this.masterGain);
    osc2.start();
    osc2.stop(this.context.currentTime + 0.02);
  }

  /**
   * Start slide sound - gentle paper friction
   * @returns Stop function
   */
  playSlideStart(): () => void {
    if (!this.context || !this.masterGain) return () => {};

    // Create noise source
    const bufferSize = this.context.sampleRate * 2;
    const buffer = this.context.createBuffer(1, bufferSize, this.context.sampleRate);
    const data = buffer.getChannelData(0);

    // Generate brown noise (lower frequency)
    let lastOut = 0;
    for (let i = 0; i < bufferSize; i++) {
      const white = Math.random() * 2 - 1;
      lastOut = (lastOut + (0.02 * white)) / 1.02;
      data[i] = lastOut * 3.5;
    }

    const source = this.context.createBufferSource();
    source.buffer = buffer;
    source.loop = true;

    const gain = this.context.createGain();
    const filter = this.context.createBiquadFilter();

    // Low frequency bandpass for soft rustle
    filter.type = 'bandpass';
    filter.frequency.value = 400;
    filter.Q.value = 0.3;

    gain.gain.value = 0.08 * this.sfxVolume;

    source.connect(filter);
    filter.connect(gain);
    gain.connect(this.masterGain);

    source.start();
    this.noiseSource = source;

    const ctx = this.context;
    return () => {
      gain.gain.linearRampToValueAtTime(0, ctx.currentTime + 0.15);
      const src = source;
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            try {
              src.stop();
              src.disconnect();
            } catch {
              // Already stopped
            }
          });
        });
      });
    };
  }

  /**
   * Lock sound - solid placement thud
   */
  playLock(): void {
    if (!this.context || !this.masterGain) return;

    // Deep satisfying thump
    const osc = this.context.createOscillator();
    const gain = this.context.createGain();

    osc.type = 'sine';
    osc.frequency.setValueAtTime(90, this.context.currentTime);
    osc.frequency.exponentialRampToValueAtTime(40, this.context.currentTime + 0.15);

    gain.gain.setValueAtTime(0.4 * this.sfxVolume, this.context.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, this.context.currentTime + 0.2);

    osc.connect(gain);
    gain.connect(this.masterGain);
    osc.start();
    osc.stop(this.context.currentTime + 0.2);

    // Short click for tactile feel
    const click = this.context.createOscillator();
    const clickGain = this.context.createGain();

    click.type = 'square';
    click.frequency.value = 200;

    clickGain.gain.setValueAtTime(0.08 * this.sfxVolume, this.context.currentTime);
    clickGain.gain.exponentialRampToValueAtTime(0.001, this.context.currentTime + 0.015);

    click.connect(clickGain);
    clickGain.connect(this.masterGain);
    click.start();
    click.stop(this.context.currentTime + 0.015);
  }

  /**
   * Spawn sound - quick pop/appear
   */
  playSpawn(): void {
    if (!this.context || !this.masterGain) return;

    // Quick ascending "pop" tone
    const osc = this.context.createOscillator();
    const gain = this.context.createGain();

    osc.type = 'sine';
    osc.frequency.setValueAtTime(200, this.context.currentTime);
    osc.frequency.exponentialRampToValueAtTime(400, this.context.currentTime + 0.04);
    osc.frequency.exponentialRampToValueAtTime(250, this.context.currentTime + 0.1);

    gain.gain.setValueAtTime(0.2 * this.sfxVolume, this.context.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, this.context.currentTime + 0.12);

    osc.connect(gain);
    gain.connect(this.masterGain);
    osc.start();
    osc.stop(this.context.currentTime + 0.12);
  }

  /**
   * Fall/whoosh sound - gentle descending tone
   */
  playFall(): void {
    if (!this.context || !this.masterGain) return;

    const osc = this.context.createOscillator();
    const gain = this.context.createGain();
    const filter = this.context.createBiquadFilter();

    osc.type = 'sine';
    osc.frequency.setValueAtTime(200, this.context.currentTime);
    osc.frequency.exponentialRampToValueAtTime(50, this.context.currentTime + 0.4);

    filter.type = 'lowpass';
    filter.frequency.value = 250;

    gain.gain.setValueAtTime(0.2 * this.sfxVolume, this.context.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, this.context.currentTime + 0.4);

    osc.connect(filter);
    filter.connect(gain);
    gain.connect(this.masterGain);

    osc.start();
    osc.stop(this.context.currentTime + 0.4);
  }

  /**
   * Win celebration - gentle ascending tones
   */
  playWin(): void {
    if (!this.context || !this.masterGain) return;

    // Lower, gentler notes
    const notes = [196, 247, 294, 392]; // G3, B3, D4, G4
    const baseTime = this.context.currentTime;

    notes.forEach((freq, i) => {
      const osc = this.context!.createOscillator();
      const gain = this.context!.createGain();
      const filter = this.context!.createBiquadFilter();

      osc.type = 'sine';
      osc.frequency.value = freq;

      filter.type = 'lowpass';
      filter.frequency.value = 500;

      const startTime = baseTime + i * 0.2;
      const volume = 0.15 * this.sfxVolume;

      gain.gain.setValueAtTime(0, startTime);
      gain.gain.linearRampToValueAtTime(volume, startTime + 0.08);
      gain.gain.exponentialRampToValueAtTime(0.001, startTime + 1.2);

      osc.connect(filter);
      filter.connect(gain);
      gain.connect(this.masterGain!);

      osc.start(startTime);
      osc.stop(startTime + 1.2);
    });
  }

  /**
   * Button tap sound - crisp click
   */
  playButtonTap(): void {
    if (!this.context || !this.masterGain) return;

    // Quick two-tone click
    const osc = this.context.createOscillator();
    const gain = this.context.createGain();

    osc.type = 'sine';
    osc.frequency.setValueAtTime(600, this.context.currentTime);
    osc.frequency.exponentialRampToValueAtTime(400, this.context.currentTime + 0.03);

    gain.gain.setValueAtTime(0.18 * this.sfxVolume, this.context.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, this.context.currentTime + 0.05);

    osc.connect(gain);
    gain.connect(this.masterGain);
    osc.start();
    osc.stop(this.context.currentTime + 0.05);
  }

  // ============= Volume Controls =============

  setMusicVolume(_volume: number): void {
    // No ambient music - this is a no-op
  }

  setSFXVolume(volume: number): void {
    this.sfxVolume = Math.max(0, Math.min(1, volume));
  }

  setMuted(muted: boolean): void {
    if (this.masterGain) {
      this.masterGain.gain.value = muted ? 0 : 1;
    }
  }

  getMusicVolume(): number {
    return 0; // No ambient music
  }

  getSFXVolume(): number {
    return this.sfxVolume;
  }

  /**
   * Cleanup resources
   */
  destroy(): void {
    this.stopAmbient();
    if (this.noiseSource) {
      try {
        this.noiseSource.stop();
        this.noiseSource.disconnect();
      } catch {
        // Already stopped
      }
    }
    if (this.context) {
      this.context.close();
      this.context = null;
    }
  }
}
